"use client";

import * as React from "react";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
type SizeRow = {
  us: string;
  uk: string;
  eu: string;
  cm: string;
  fit: string;
  matched: boolean;
};
const brands = [{
  id: "ota",
  label: "OTA Management"
}, {
  id: "tech",
  label: "Tech Stack"
}, {
  id: "audit",
  label: "Revenue Audit"
}, {
  id: "direct",
  label: "Direct Booking"
}];
const otaRows: SizeRow[] = [{
  us: "Booking",
  uk: "Expedia",
  eu: "Agoda",
  cm: "TripAdvisor",
  fit: "High visibility focus",
  matched: false
}, {
  us: "Rate",
  uk: "Parity",
  eu: "Sync",
  cm: "Automated",
  fit: "Real-time rate updates",
  matched: true
}, {
  us: "Content",
  uk: "SEO",
  eu: "Rich",
  cm: "Media",
  fit: "Optimized property descriptions",
  matched: false
}, {
  us: "Review",
  uk: "Score",
  eu: "Boost",
  cm: "Strategy",
  fit: "Reputation management",
  matched: false
}, {
  us: "Yield",
  uk: "Mgmt",
  eu: "Dynamic",
  cm: "Pricing",
  fit: "Demand-based adjustments",
  matched: false
}];
const techRows: SizeRow[] = [{
  us: "PMS",
  uk: "Cloud",
  eu: "Mobile",
  cm: "Integrated",
  fit: "Seamless property operations",
  matched: false
}, {
  us: "Channel",
  uk: "Manager",
  eu: "Sync",
  cm: "Live",
  fit: "Zero overbooking risk",
  matched: true
}, {
  us: "Booking",
  uk: "Engine",
  eu: "Direct",
  cm: "Conversion",
  fit: "High-speed booking flow",
  matched: false
}, {
  us: "CRM",
  uk: "Guest",
  eu: "Data",
  cm: "Loyalty",
  fit: "Personalized guest journeys",
  matched: false
}, {
  us: "BI",
  uk: "Tools",
  eu: "Report",
  cm: "Insights",
  fit: "Data-driven decision making",
  matched: false
}];
const auditRows: SizeRow[] = [{
  us: "Market",
  uk: "Comp",
  eu: "Set",
  cm: "Analysis",
  fit: "Competitive positioning audit",
  matched: false
}, {
  us: "Revenue",
  uk: "Leak",
  eu: "Fix",
  cm: "Strategy",
  fit: "Identifying lost revenue",
  matched: true
}, {
  us: "Pricing",
  uk: "Audit",
  eu: "Gap",
  cm: "Check",
  fit: "Rate parity compliance",
  matched: false
}, {
  us: "OTA",
  uk: "Health",
  eu: "Score",
  cm: "Review",
  fit: "Listing performance check",
  matched: false
}, {
  us: "Tech",
  uk: "Stack",
  eu: "Audit",
  cm: "Review",
  fit: "System efficiency check",
  matched: false
}];
const directRows: SizeRow[] = [{
  us: "Website",
  uk: "Design",
  eu: "UX",
  cm: "Speed",
  fit: "High-conversion landing pages",
  matched: false
}, {
  us: "SEO",
  uk: "Local",
  eu: "Rank",
  cm: "Growth",
  fit: "Organic search dominance",
  matched: true
}, {
  us: "Ads",
  uk: "Paid",
  eu: "Media",
  cm: "ROI",
  fit: "Targeted guest acquisition",
  matched: false
}, {
  us: "Email",
  uk: "Direct",
  eu: "Sales",
  cm: "Loyalty",
  fit: "Repeat guest marketing",
  matched: false
}, {
  us: "Offers",
  uk: "Promo",
  eu: "Value",
  cm: "Direct",
  fit: "Exclusive booking incentives",
  matched: false
}];
const brandData: Record<string, SizeRow[]> = {
  ota: otaRows,
  tech: techRows,
  audit: auditRows,
  direct: directRows
};
function SizeTable({
  rows
}: {
  rows: SizeRow[];
}) {
  return <TooltipProvider delayDuration={150}>
      <Table className="border-collapse">
        <TableHeader>
          <TableRow className="border-b border-[var(--border)] hover:bg-transparent">
            <TableHead className="font-default text-xs uppercase tracking-[0.18em] text-slate-500 sticky left-0 bg-background">
              Service
            </TableHead>
            <TableHead className="font-default text-xs uppercase tracking-[0.18em] text-slate-500">
              Focus
            </TableHead>
            <TableHead className="font-default text-xs uppercase tracking-[0.18em] text-slate-500">
              Method
            </TableHead>
            <TableHead className="font-default text-xs uppercase tracking-[0.18em] text-slate-500">
              Result
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((row, i) => <TableRow key={i} data-index={i} className="border-b border-dashed border-slate-200 odd:bg-slate-50 transition-colors duration-200 hover:bg-blue-50 cursor-default">
              <TableCell className={row.matched ? "sticky left-0 bg-blue-900 text-white font-default font-semibold text-base" : "sticky left-0 bg-background font-default text-base text-slate-900"}>
                {row.us}
              </TableCell>
              <TableCell className="font-default text-base text-slate-700">
                {row.uk}
              </TableCell>
              <TableCell className="font-default text-base text-slate-700">
                {row.eu}
              </TableCell>
              <TableCell className="font-default text-base text-slate-700">
                {row.cm}
                <Tooltip>
                  <TooltipTrigger>
                    <span className="cursor-help underline decoration-dotted ml-1">?</span>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="bg-blue-900 text-white font-default text-[0.7rem] uppercase tracking-[0.12em]">
                    {row.fit}
                  </TooltipContent>
                </Tooltip>
              </TableCell>
            </TableRow>)}
        </TableBody>
      </Table>
    </TooltipProvider>;
}
export default function SystemsComparison() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>();
  return <section data-section-id="1935" id="systemscomparison" className="bg-slate-50 text-slate-900 py-24 md:py-36 px-5 md:px-10">
      <div className="mx-auto max-w-[1320px]">
        <div ref={ref} className={"relative grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-8 items-end transition-all duration-700 " + (isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6")}>
          <span aria-hidden="true" className="pointer-events-none absolute -top-10 left-0 select-none font-default font-bold text-slate-900/[0.06] leading-none text-[8rem] md:text-[12rem]">
            02
          </span>

          <div className="lg:col-span-8 relative z-10">
            <p className="font-default text-xs md:text-sm uppercase tracking-[0.22em] text-blue-700 mb-6">
              § 02 / REVENUE STRATEGY MATRIX
            </p>
            <h2 className="font-default font-semibold leading-[0.98] tracking-[-0.02em] text-[clamp(2rem,4.5vw,3.75rem)] text-slate-900">
              Revenue &middot; Tech &middot; Growth,{" "}
              <span className="relative inline-block">
                Side
                <span className="absolute -bottom-1 left-0 h-[3px] w-full bg-blue-600" />
                <span className="absolute -bottom-1 right-[-6px] h-[7px] w-[7px] rounded-lg bg-blue-600" />
              </span>{" "}
              By Side
            </h2>
            <p className="mt-6 max-w-xl font-default text-base md:text-lg leading-relaxed text-slate-600">
              Compare our core revenue management pillars. Hover over any service row 
              to see how Sunil Singh optimizes your property's performance.
            </p>
          </div>

          <div className="lg:col-span-4 relative z-10">
            <Tabs defaultValue="ota" className="w-full">
              <TabsList className="flex w-full flex-wrap h-auto gap-1 bg-slate-200 p-1 rounded-lg">
                {brands.map((brand, i) => <TabsTrigger key={brand.id} data-index={i} value={brand.id} className="flex-1 font-default text-xs uppercase tracking-[0.1em] data-[state=active]:bg-blue-900 data-[state=active]:text-white rounded-lg">
                    {brand.label}
                  </TabsTrigger>)}
              </TabsList>

              {brands.map((brand, i) => <TabsContent key={brand.id} data-index={i} value={brand.id} className="mt-10 animate-in fade-in-0 duration-300">
                  <div className="block md:hidden">
                    <div className="rounded-lg border border-slate-200 p-1">
                      <ScrollArea className="w-full whitespace-nowrap">
                        <div className="min-w-[480px] p-3">
                          <SizeTable rows={brandData[brand.id]} />
                        </div>
                        <ScrollBar orientation="horizontal" />
                      </ScrollArea>
                    </div>
                  </div>
                </TabsContent>)}
            </Tabs>
          </div>
        </div>

        <div className="my-10 hidden md:block border-t border-slate-200" />

        <div className="hidden md:block">
          <Tabs defaultValue="ota" className="w-full">
            <TabsList className="mb-8 inline-flex h-auto gap-1 bg-slate-200 p-1 rounded-lg">
              {brands.map((brand, i) => <TabsTrigger key={brand.id} data-index={i} value={brand.id} className="font-default text-xs uppercase tracking-[0.12em] px-5 data-[state=active]:bg-blue-900 data-[state=active]:text-white rounded-lg">
                  {brand.label}
                </TabsTrigger>)}
            </TabsList>

            {brands.map((brand, i) => <TabsContent key={brand.id} data-index={i} value={brand.id} className="animate-in fade-in-0 duration-300">
                <div className="rounded-lg border border-slate-200 p-4 md:p-6 bg-white shadow-md">
                  <SizeTable rows={brandData[brand.id]} />
                </div>
                <p className="mt-4 font-default text-[0.7rem] uppercase tracking-[0.12em] text-slate-500">
                  Highlighted rows indicate our primary focus areas for maximum revenue impact.
                </p>
              </TabsContent>)}
          </Tabs>
        </div>
      </div>
    </section>;
}